import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import styled from "styled-components";
import { Modal } from "react-bootstrap";
import InvestorPageShell from "../../../components/Investor/InvestorPageShell";
import KycBanner from "../../../components/Investor/KycBanner";
import RealEstatePropertyCard from "../../../components/Investor/RealEstatePropertyCard";
import RealEstateRequestModal from "../../../components/Investor/RealEstateRequestModal";
import InvestorRequestsList from "../../../components/Investor/InvestorRequestsList";
import { useInvestorGate } from "../../../hooks/useInvestorGate";
import { useInvestorSession } from "../../../hooks/useInvestorSession";
import { useCompanyCapabilities } from "../../../hooks/useCompanyCapabilities";
import {
  REAL_ESTATE_CATALOGUE_NOTE,
  REAL_ESTATE_PROPERTIES,
  getRealEstatePropertyById,
} from "../../../data/investor/realEstateCatalogue";
import { useListRealEstateRequestsQuery } from "../../../store/investor/investorRealEstateApi";
import {
  InvestorEmptyState,
  InvestorApiErrorState,
} from "../../../components/Investor/InvestorPlatformState";
import { CardComp } from "../../../components";
import { formatMoney } from "../../../utils/currencyFormat";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";

const SectionTitle = styled.h5`
  font-weight: 600;
  color: #383838;
  margin-bottom: 0.75rem;
`;

export default function RealEstateRequestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ensureOrderAccess, kycStatus } = useInvestorGate();
  const { profile } = useInvestorSession();
  const { realEstate } = useCompanyCapabilities();

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [detailProperty, setDetailProperty] = useState(null);

  const {
    data: requestsData,
    isFetching,
    isError,
    refetch,
  } = useListRealEstateRequestsQuery({ per_page: 5 }, { skip: !realEstate });

  const myRequests = requestsData?.items || [];

  useEffect(() => {
    const intent = searchParams.get("intent");
    const itemId = searchParams.get("itemId");
    if (intent === "request-property" && itemId) {
      const property = getRealEstatePropertyById(itemId);
      if (property) {
        openRequest(property);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRequest = (property) => {
    if (!ensureOrderAccess("/invest/real-estate", { intent: "request-property", itemId: property.id })) {
      toast("Complete KYC to submit a property request");
      return;
    }
    setSelectedProperty(property);
    setShowRequestModal(true);
  };

  if (!realEstate) {
    return (
      <InvestorPageShell
        title="Real estate"
        subtitle="Express interest in property opportunities offered through your cooperative."
      >
        <CardComp cardClassName="border-0 shadow-sm rounded-4 text-center py-5">
          <h5 className="fw-bold mb-2">Real Estate is not enabled for this organization.</h5>
          <p className="text-muted mb-0">Contact your cooperative office for assistance.</p>
        </CardComp>
      </InvestorPageShell>
    );
  }

  return (
    <InvestorPageShell
      title="Real estate"
      subtitle="Browse properties and submit an interest request. Staff will follow up — no payment in this step."
    >
      <KycBanner status={kycStatus} redirectTo="/invest/real-estate" intent="request-property" />

      <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-4">
        <p className="small mb-0" style={{ color: "#525252" }}>
          {REAL_ESTATE_CATALOGUE_NOTE}
        </p>
      </CardComp>

      <SectionTitle>Available properties</SectionTitle>
      {!REAL_ESTATE_PROPERTIES.length ? (
        <InvestorEmptyState message="No property opportunities are available yet." />
      ) : (
        <div className="row g-3 mb-5">
          {REAL_ESTATE_PROPERTIES.map((property) => (
            <div key={property.id} className="col-12 col-md-6 col-xl-4">
              <RealEstatePropertyCard
                property={property}
                onRequest={openRequest}
                onView={setDetailProperty}
              />
            </div>
          ))}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <SectionTitle className="mb-0">My property requests</SectionTitle>
        <Link to="/invest/requests?filter=real_estate" className="small fw-semibold" style={{ color: "var(--color-secondary)" }}>
          View all →
        </Link>
      </div>

      {isFetching ? (
        <TableSkeletonLoader rows={3} columns={2} />
      ) : isError ? (
        <InvestorApiErrorState
          message="We couldn’t load your property requests. Please try again."
          onRetry={() => refetch()}
        />
      ) : (
        <InvestorRequestsList
          requests={myRequests}
          emptyMessage="No property requests yet."
          linkToRequests={false}
        />
      )}

      <RealEstateRequestModal
        show={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        property={selectedProperty}
        profile={profile}
        onSuccess={(created) => {
          if (created?.reference) {
            navigate(`/invest/requests/${encodeURIComponent(created.reference)}`);
          }
        }}
      />

      <PropertyDetailModal property={detailProperty} onClose={() => setDetailProperty(null)} onRequest={openRequest} />
    </InvestorPageShell>
  );
}

function PropertyDetailModal({ property, onClose, onRequest }) {
  if (!property) return null;
  return (
    <Modal show={Boolean(property)} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="h5 fw-bold">{property.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-2">
          {property.landType} · {property.location}
        </p>
        <p className="fw-bold mb-3" style={{ color: "var(--color-primary)" }}>
          {formatMoney(property.pricePerUnitNgn)}
          <span className="text-muted fw-normal small"> / {property.unitLabel}</span>
        </p>
        <p style={{ color: "#45464e" }}>{property.fullDescription}</p>
        <p className="small text-muted mb-1">
          <strong>Documentation:</strong> {property.documentation}
        </p>
        <p className="small text-muted mb-3">
          <strong>Inspections:</strong> {property.inspectionHint}
        </p>
        <p className="fw-semibold small mb-2">How we help</p>
        <ul className="small text-muted mb-0">
          {(property.helpsWith || []).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
          Close
        </button>
        <button
          type="button"
          className="btn"
          style={{ background: "var(--color-primary)", color: "#fff" }}
          onClick={() => {
            onClose();
            onRequest(property);
          }}
        >
          Request property
        </button>
      </Modal.Footer>
    </Modal>
  );
}
